import axios from 'axios';

import { env } from 'config';
import { OrderType, UserRole } from 'enums';
import {
  CascadesCashbackData,
  PaginatedData,
  QueryParams,
  Shop,
  ShopIntegrationConfig,
  ShopSecrets,
} from 'types';
import { apiUtils } from 'utils';

import { createCrudApi } from './crud.api';

const path = '/shop';
const url = `${env.apiUrl}${path}`;

const getReferrals = async (): Promise<Shop[]> =>
  (await axios.get(`${url}/referrals`)).data;

const getPayinTradeMethodIds = async (shopId: string): Promise<string[]> =>
  (await axios.get(`${url}/admin/${shopId}/payin-trade-methods`)).data;

const getIntegrationConfig =
  (shopId: string) =>
  async (params?: Partial<QueryParams>): Promise<ShopIntegrationConfig> => {
    const res = await apiUtils.getWithQueryParams(
      `${url}/${shopId}/integrations`,
      params,
    );
    return res.data;
  };

const updateIntegrationConfig = async ({
  shopId,
  data,
}: {
  shopId: string;
  data: ShopIntegrationConfig;
}): Promise<ShopIntegrationConfig> =>
  (await axios.patch(`${url}/${shopId}/integrations`, data)).data;

const getPayoutConfigAsMerchant = async (
  shopId: string,
): Promise<{ tradeMethodIds: string[] }> =>
  (await axios.get(`${url}/merchant/${shopId}/payout-config`)).data;

const getCascadesCashbackForOrderType =
  (orderType: OrderType) =>
  async (params?: QueryParams): Promise<PaginatedData<CascadesCashbackData>> =>
    (
      await apiUtils.getWithQueryParams(`${url}/cascades/paginated`, {
        ...params,
        orderType,
      })
    ).data;

const getSecretsAsRole =
  (role: UserRole) =>
  async (shopId: string): Promise<ShopSecrets> =>
    (await axios.post(`${url}/${role}/${shopId}/secrets`)).data;

const crudApi = createCrudApi<Shop>(path);

export const shopsApi = {
  ...crudApi,
  getAllAsMerchant: crudApi.getAllAsRole(UserRole.Merchant), // just a shortcut as it's often in use
  getReferrals,
  getPayinTradeMethodIds,
  getIntegrationConfig,
  updateIntegrationConfig,
  getCascadesCashbackForOrderType,
  getPayoutConfigAsMerchant,
  getSecretsAsRole,
};
